import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { authApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Loader2, ShieldCheck, ShieldAlert } from 'lucide-react';
import { Button } from '../components/ui/button';

/**
 * /auth/magic?token=…
 *
 * Lands the user from a magic-link click. Verifies the token via the
 * backend, which sets the httpOnly auth cookie. Then refreshes the
 * AuthContext (so `user`/`isAuthenticated` flip) and bounces them into
 * /dashboard. Errors render an inline retry CTA.
 */
const MagicLinkVerify = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshProfile } = useAuth();
  const [state, setState] = useState({ status: 'verifying', message: '' });
  // Guard against StrictMode double-invoke in dev — verify is single-use,
  // so a second call would always 400 with "already used".
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const token = params.get('token');
    if (!token) {
      setState({ status: 'error', message: 'Missing sign-in token.' });
      return;
    }

    (async () => {
      try {
        const res = await authApi.verifyMagicLink(token);
        if (typeof refreshProfile === 'function') {
          await refreshProfile();
        }
        setState({
          status: 'ok',
          message: `Welcome${res?.user?.username ? `, ${res.user.username}` : ''}!`,
        });
        // Small delay so the success state is visible.
        setTimeout(() => navigate('/dashboard', { replace: true }), 900);
      } catch (err) {
        setState({
          status: 'error',
          message: err?.message || 'Sign-in link is invalid or has expired.',
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-4">
      <div
        className="w-full max-w-md bg-[#1A1A1A] border border-gray-800 rounded-lg p-8 text-center shadow-2xl"
        data-testid="magic-verify-card"
      >
        {state.status === 'verifying' && (
          <>
            <Loader2 className="w-10 h-10 text-[#00FF00] animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-semibold mb-2">Signing you in…</h1>
            <p className="text-sm text-gray-400">Verifying your magic link.</p>
          </>
        )}

        {state.status === 'ok' && (
          <>
            <ShieldCheck
              className="w-12 h-12 text-[#00FF00] mx-auto mb-4"
              data-testid="magic-verify-success-icon"
            />
            <h1 className="text-xl font-semibold mb-2" data-testid="magic-verify-success-msg">
              {state.message}
            </h1>
            <p className="text-sm text-gray-400">Taking you to your dashboard…</p>
          </>
        )}

        {state.status === 'error' && (
          <>
            <ShieldAlert
              className="w-12 h-12 text-[#FF0055] mx-auto mb-4"
              data-testid="magic-verify-error-icon"
            />
            <h1 className="text-xl font-semibold mb-2">We couldn&apos;t sign you in</h1>
            <p
              className="text-sm text-gray-400 mb-6"
              data-testid="magic-verify-error-msg"
            >
              {state.message}
            </p>
            <Button
              onClick={() => navigate('/', { replace: true })}
              data-testid="magic-verify-retry-btn"
              className="bg-[#00FF00] text-black hover:bg-[#00DD00] font-semibold"
            >
              Request a new link
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default MagicLinkVerify;
